// CLIENT-SIDE ONLY - No Firebase imports, just helper functions for UI logic
import { RESCHEDULE_NOTICE_HOURS } from "./meetingPolicy.js";

/**
 * Calculate hours until meeting start time
 */
export const getHoursUntilMeeting = (meetingTime) => {
  if (!meetingTime) return null;

  const now = new Date();
  const meeting = meetingTime.toDate
    ? meetingTime.toDate()
    : new Date(meetingTime);
  const diffMs = meeting.getTime() - now.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  return Math.max(0, diffHours);
};

/**
 * Check if user can cancel meeting based on role, status, and timing
 */
export const canCancelMeeting = (meetingData, userRole, userSub) => {
  const { status, meeting_time, seeker, sharer } = meetingData;

  // For accepted meetings - only seeker can cancel
  if (status === "accepted") {
    return userRole === "seeker" && seeker?.sub === userSub;
  }

  // For scheduled or launching meetings
  if (status === "scheduled" || status === "launching") {
    if (userRole === "sharer") {
      // Sharer can cancel anytime
      return sharer?.sub === userSub;
    }

    if (userRole === "seeker") {
      return seeker?.sub === userSub;
    }
  }

  return false;
};

/**
 * Check if user can reschedule meeting
 */
export const canRescheduleMeeting = (meetingData, userRole, userSub) => {
  const { status, meeting_time, seeker, session } = meetingData;

  if (status !== "scheduled") return false;
  if (userRole !== "seeker" || seeker?.sub !== userSub) return false;

  const hoursUntil = getHoursUntilMeeting(meeting_time);
  if (hoursUntil <= RESCHEDULE_NOTICE_HOURS) return false;

  const rescheduleCount = session?.reschedule_number || 0;
  return rescheduleCount < 2;
};

/**
 * Check if reschedule button should be shown (regardless of policy eligibility)
 */
export const shouldShowRescheduleButton = (meetingData, userRole, userSub) => {
  const { status, seeker } = meetingData;
  return (
    status === "scheduled" && userRole === "seeker" && seeker?.sub === userSub
  );
};

/**
 * Get the reason why rescheduling is blocked (returns null if allowed)
 */
export const getRescheduleBlockReason = (meetingData) => {
  const { meeting_time, session } = meetingData;

  const rescheduleCount = session?.reschedule_number || 0;
  if (rescheduleCount >= 2) {
    return "You've used all 2 reschedule attempts for this meeting. No further rescheduling is allowed.";
  }

  const hoursUntil = getHoursUntilMeeting(meeting_time);
  if (hoursUntil <= RESCHEDULE_NOTICE_HOURS) {
    return `Rescheduling is not available within ${RESCHEDULE_NOTICE_HOURS} hours of the meeting start time.`;
  }

  return null;
};

/**
 * Get reschedule information for UI
 */
export const getRescheduleInfo = (meetingData) => {
  if (!meetingData) {
    return {
      rescheduleCount: 0,
      remainingReschedules: 2,
      canReschedule: true,
      isFirstReschedule: true,
      isFinalReschedule: false,
      warningMessage: "You can reschedule 1 more time after this",
    };
  }
  const rescheduleCount = meetingData.session?.reschedule_number || 0;
  const remainingReschedules = Math.max(0, 2 - rescheduleCount);

  return {
    rescheduleCount,
    remainingReschedules,
    canReschedule: remainingReschedules > 0,
    isFirstReschedule: rescheduleCount === 0,
    isFinalReschedule: rescheduleCount === 1,
    warningMessage:
      rescheduleCount === 0
        ? "You can reschedule 1 more time after this"
        : rescheduleCount === 1
          ? "This is your final reschedule opportunity"
          : "No more reschedules available",
  };
};

/**
 * Calculate refund amount based on meeting status, role, and timing
 */
export const calculateRefundAmount = (meetingData, userRole) => {
  const { status, session, meeting_time } = meetingData;
  const sessionRate = session?.session_rate || 0;

  // No refund for accepted meetings (payment not processed yet)
  if (status === "accepted") {
    return 0;
  }

  // For scheduled or launching meetings
  if (status === "scheduled" || status === "launching") {
    if (userRole === "sharer") {
      // Sharer cancellation = full refund to seeker
      return sessionRate;
    }

    if (userRole === "seeker") {
      const hoursUntil = getHoursUntilMeeting(meeting_time);

      if (hoursUntil > 24) {
        // More than 24h = full refund
        return sessionRate;
      } else {
        // Within 24h = 50% refund
        return Math.floor(sessionRate * 0.5);
      }
    }
  }

  return 0;
};

/**
 * Get cancellation message based on meeting status and role
 */
export const getCancellationMessage = (meetingData, userRole) => {
  const { status } = meetingData;
  const refundAmount = calculateRefundAmount(meetingData, userRole);
  const hoursUntil = getHoursUntilMeeting(meetingData.meeting_time);
  const sessionRate = meetingData?.session?.session_rate || 0;
  const isFreeSession = sessionRate === 0;
  const hasValidTimeUntil =
    typeof hoursUntil === "number" && !Number.isNaN(hoursUntil);

  const formatTimeUntilStart = () => {
    if (!hasValidTimeUntil) return "soon";
    if (hoursUntil < 1) return "in less than 1 hour";
    if (hoursUntil < 24) return `in about ${Math.ceil(hoursUntil)} hours`;

    const days = Math.floor(hoursUntil / 24);
    const remHours = Math.ceil(hoursUntil % 24);

    if (days >= 2 || remHours === 0) {
      return `in about ${days} day${days > 1 ? "s" : ""}`;
    }

    return `in about 1 day and ${remHours} hour${remHours > 1 ? "s" : ""}`;
  };

  if (status === "accepted") {
    return {
      title: "Cancel Request",
      message: "Cancel this request?",
      confirmText: "Cancel Request",
      type: "warning",
    };
  }

  if (status === "scheduled" || status === "launching") {
    if (userRole === "sharer") {
      return {
        title: "Cancel Meeting",
        message: "Cancel this meeting? This affects your standing.",
        confirmText: "Cancel Meeting",
        type: "info",
      };
    }

    if (userRole === "seeker") {
      const isLateCancelWindow = hasValidTimeUntil && hoursUntil <= 24;
      let message;

      if (isFreeSession) {
        message = isLateCancelWindow
          ? "Late cancels may pause rebooking."
          : "You can book this person again.";
      } else {
        message = isLateCancelWindow
          ? `Late cancels may reduce your refund to $${refundAmount}.`
          : `Your refund is $${refundAmount}.`;
      }

      return {
        title: "Cancel Meeting",
        message,
        confirmText: "Cancel Meeting",
        type: "warning",
      };
    }
  }

  return {
    title: "Cancel Meeting",
    message: "Cancel this meeting?",
    confirmText: "Cancel",
    type: "warning",
  };
};

/**
 * Get available actions for a meeting based on user role and meeting status
 */
export const getAvailableActions = (meetingData, userRole, userSub) => {
  const actions = {
    canCancel: false,
    canReschedule: false,
    showRescheduleButton: false,
    rescheduleBlockReason: null,
    cancelMessage: null,
    hoursUntilMeeting: null,
  };

  if (!meetingData) return actions;

  actions.canCancel = canCancelMeeting(meetingData, userRole, userSub);
  actions.canReschedule = canRescheduleMeeting(meetingData, userRole, userSub);
  actions.showRescheduleButton = shouldShowRescheduleButton(
    meetingData,
    userRole,
    userSub,
  );
  actions.rescheduleBlockReason =
    actions.showRescheduleButton && !actions.canReschedule
      ? getRescheduleBlockReason(meetingData)
      : null;
  actions.cancelMessage = getCancellationMessage(meetingData, userRole);
  actions.hoursUntilMeeting = getHoursUntilMeeting(meetingData.meeting_time);

  return actions;
};

/**
 * Client-side API call to cancel meeting
 */
export const cancelMeetingAPI = async (meetingId, userRole) => {
  try {
    const response = await fetch("/api/meeting/cancel-advanced", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        meetingId,
        userRole,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to cancel meeting");
    }

    return await response.json();
  } catch (error) {
    console.error("Error calling cancel meeting API:", error);
    throw error;
  }
};

/**
 * Client-side API call to reschedule meeting
 */
export const rescheduleMeetingAPI = async (
  meetingId,
  newDateTime,
  userRole,
) => {
  try {
    const response = await fetch("/api/meeting/reschedule-advanced", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        meetingId,
        newDateTime,
        userRole,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to reschedule meeting");
    }

    return await response.json();
  } catch (error) {
    console.error("Error calling reschedule meeting API:", error);
    throw error;
  }
};

/**
 * Client-side API call to check active restrictions
 */
export const checkActiveRestrictionAPI = async (sharerId) => {
  try {
    const response = await fetch(
      `/api/meeting/check-restriction?sharerId=${sharerId}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to check restriction");
    }

    const data = await response.json();
    return data.hasRestriction || false;
  } catch (error) {
    console.error("Error checking restriction:", error);
    return false; // Default to no restriction on error
  }
};
